import { Link } from "components/DesignSystem/Link/Link";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useRouteMatch } from "react-router-dom";
import { useGetCreditsQuery } from "store/apis/refrral";
import { setCreditAmountToAdd, setPaymentTitle } from "store/slices/credit";
import { AddCredits } from "./billing/Credits/AddCredit";
import { AdminAddCreditsModal } from "./billing/Credits/AdminAddCreditsModal";
import { AutoPayCreditModal } from "./billing/Credits/AutoPayCreditModal";
import { Button } from "./DesignSystem/Button/Button";
import { InkleCoin } from "./icons/InkleCoin";
import { Tag } from "./DesignSystem/Tag/Tag";
import { useRoleBasedView } from "hooks/useRoleBasedView";

export const CreditsCard = ({ refetch }: { refetch?: () => void }) => {
  const { uuid: groupId, entities } = useCurrentGroupContext();
  const { isAdmin } = useRoleBasedView();
  const adminCreditsModal = useModal();
  const { data } = useGetCreditsQuery({ groupId }, { skip: !groupId });
  const { total_credits } = data || {};

  const addCredits = useQuery().get("add_credits");
  const { update } = useUpdateQuery();
  const {
    open: openPaymentModal,
    close: closePaymentModal,
    isOpen: showPaymentModal,
  } = useModal();

  useEffect(() => {
    if (addCredits) {
      openPaymentModal();
    }
  }, [addCredits]);

  const autoPayCreditModal = useModal();
  const query = useQuery();
  const creditsToPurchase = query.get("credits_to_purchase");
  const dispatch = useDispatch();
  const entityId = query.get("entity");
  const currentEntity = entities.find(({ uuid }) => entityId === uuid);
  const { url } = useRouteMatch();
  const location = useLocation();

  useEffect(() => {
    if (creditsToPurchase !== null) {
      dispatch(setCreditAmountToAdd(+creditsToPurchase));
      dispatch(setPaymentTitle("Inkle Credits"));
      openPaymentModal();
    }
  }, [creditsToPurchase]);

  const closePaymentModalWithReset = () => {
    update({
      query: "add_credits",
      value: null,
    });
    closePaymentModal();
  };

  const seperateByCommas = (num: number) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const isAutoPayEnabled = currentEntity?.autopay_credits;
  const autoPayText = isAutoPayEnabled ? "Autopay enabled" : "Autopay disabled";
  const tagType = isAutoPayEnabled ? "green" : "gray";

  return (
    <>
      <div className="">
        <div className="t-min-h-[146px] t-w-full t-bg-refferal-gradient t-rounded-lg t-border t-border-solid t-border-neutral-10 t-flex t-gap-4 t-p-6 sm:t-flex-col sm:t-p-4">
          <div className="t-min-w-[264px] sm:t-min-w-0">
            <div className="t-text-body-sm t-text-text-30">Inkle Credits</div>
            <div className="t-flex t-items-center t-gap-1.5 t-mt-1.5">
              <InkleCoin />
              <div className="t-text-subtitle-lg">
                ${seperateByCommas(total_credits || 0)}
              </div>
            </div>
            <div className="t-mt-3"></div>
            <Tag size="small" rounded tagType={tagType}>
              {autoPayText}
            </Tag>
          </div>
          <div className="t-px-6 t-border-0 t-border-l t-border-solid t-border-neutral-10 t-flex t-flex-col sm:t-px-0 sm:t-pt-4 sm:t-border-l-0 sm:t-border-t">
            <div className="t-text-subtext t-text-text-100">
              Inkle Credits work just like cash
            </div>
            <div className="t-mt-1.5 t-mb-4 t-text-body-sm t-text-text-30">
              Use them across filings, services, and tasks. Add more anytime—or
              earn them by{" "}
              <span className="t-text-purple">
                <a
                  href={
                    url.replace("credits", "referralrewards") + location.search
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="t-text-purple t-no-underline"
                >
                  referring a founder
                </a>
              </span>
              .<br />
              With autopay on, your credits are used automatically at checkout.
            </div>
            <div className="t-flex t-items-center t-gap-2">
              <Button
                onClick={openPaymentModal}
                size="small"
                customType="primary"
              >
                Purchase credits
              </Button>
              {isAdmin && (
                <Button
                  onClick={adminCreditsModal.open}
                  size="small"
                  customType="secondary"
                >
                  Add promotion credits
                </Button>
              )}
              <Button
                onClick={autoPayCreditModal.open}
                size="small"
                customType="secondary"
              >
                Autopay settings
              </Button>
            </div>
          </div>
        </div>
        <AddCredits
          showPaymentModal={showPaymentModal}
          closePaymentModal={closePaymentModalWithReset}
          refetch={refetch}
        />
      </div>
      {autoPayCreditModal && (
        <AutoPayCreditModal
          isOpen={autoPayCreditModal.isOpen}
          close={autoPayCreditModal.close}
        />
      )}
      {isAdmin && (
        <AdminAddCreditsModal
          isOpen={adminCreditsModal.isOpen}
          close={adminCreditsModal.close}
        />
      )}
    </>
  );
};
